<script>
import { debounce } from '@chatwoot/utils';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

export default {
  components: {
    SingleSelect,
    WootMessageEditor,
  },
  props: {
    inboxes: { type: Array, required: true },
    modelValue: { type: Object, required: true },
    dropdownMaxHeight: { type: String, default: 'max-h-80' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      selectedInbox: null,
      selectedContact: null,
      message: '',
      contactOptions: [],
      isSearchingContacts: false,
    };
  },
  mounted() {
    const {
      inbox_id: inboxId,
      contact_id: contactId,
      contact_name: contactName,
      message,
    } = this.modelValue || {};

    this.selectedInbox = inboxId
      ? this.inboxes.find(inbox => inbox.id === inboxId) || null
      : null;
    this.selectedContact = contactId
      ? { id: contactId, name: contactName }
      : null;
    this.message = message || '';

    this.searchContacts = createContactSearcher();
    this.debouncedSearch = debounce(this.performSearch, 300);
  },
  methods: {
    async performSearch(query) {
      const contacts = await this.searchContacts(query, {
        skipMinLength: true,
        reachableOnly: false,
      });
      // null means the request was aborted by a newer search
      if (contacts === null) return;
      this.contactOptions = contacts.map(contact => ({
        id: contact.id,
        name:
          contact.name ||
          contact.email ||
          contact.phoneNumber ||
          `#${contact.id}`,
      }));
      this.isSearchingContacts = false;
    },
    onSearchContacts(query) {
      if (!query.trim()) {
        this.contactOptions = [];
        this.isSearchingContacts = false;
        return;
      }
      this.isSearchingContacts = true;
      this.debouncedSearch(query);
    },
    updateValue() {
      this.$emit('update:modelValue', {
        inbox_id: this.selectedInbox?.id,
        contact_id: this.selectedContact?.id,
        contact_name: this.selectedContact?.name,
        message: this.message,
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <SingleSelect
        v-model="selectedInbox"
        :options="inboxes"
        :placeholder="$t('AUTOMATION.ACTION.CONTACT_MESSAGE_INBOX_PLACEHOLDER')"
        :dropdown-max-height="dropdownMaxHeight"
        @update:model-value="updateValue"
      />
      <SingleSelect
        v-model="selectedContact"
        async-search
        :is-searching="isSearchingContacts"
        :options="contactOptions"
        :placeholder="$t('AUTOMATION.ACTION.CONTACT_MESSAGE_CONTACT_PLACEHOLDER')"
        :search-placeholder="
          $t('AUTOMATION.ACTION.CONTACT_MESSAGE_CONTACT_SEARCH_PLACEHOLDER')
        "
        :dropdown-max-height="dropdownMaxHeight"
        @search="onSearchContacts"
        @update:model-value="updateValue"
      />
    </div>
    <WootMessageEditor
      v-model="message"
      rows="4"
      enable-variables
      :placeholder="$t('AUTOMATION.ACTION.CONTACT_MESSAGE_INPUT_PLACEHOLDER')"
      class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      @update:model-value="updateValue"
    />
  </div>
</template>
